import { type CompanyTag, type Currency, type FundingRound, type Headcount } from "@prisma/client";
import { map } from "bluebird";
import { type AppContext } from "~/lib/context";
import { chain } from "~/lib/lodash";
import { hasInsufficientDataQuality } from "~/services/dataQuality";
import { type Measure, hasJob } from "~/services/employee";
import { type EmployeeLevel } from "~/services/employee/employeeLevel";
import { type WeightedJob } from "~/services/job";
import { computeDatasetEmployeesCompensationData } from "~/services/market-data/dataset-employee/compute-compensations/computeDatasetEmployeesCompensationData";
import { type MarketDataDatasetEmployee } from "~/services/market-data/dataset-employee/getMarketDataDatasetEmployees";
import { type EmployeeLocationsForPredictiveBenchmark } from "~/services/market-data/getMarketDataPredictiveBenchmark";
import { type PercentileBounds } from "~/services/percentiles";

type ComputeEmployeesCompensationDataOptions<T extends Measure> = {
  currency: Currency;
  measures: T[];
  level?: EmployeeLevel;
  locations: EmployeeLocationsForPredictiveBenchmark;
  percentiles: PercentileBounds;
  targetPercentile?: number;
  fallbackToPredictiveData?: boolean;
  fundingRounds?: FundingRound[];
  headcount?: Headcount | null;
  industry?: CompanyTag | null;
};

type ComputeMarketCompensationDataOptions<T extends Measure> = ComputeEmployeesCompensationDataOptions<T> & {
  weightedJobs: WeightedJob[];
};

export const computeJobsCompensationData = async <T extends Measure>(
  ctx: AppContext,
  datasetEmployees: MarketDataDatasetEmployee[],
  options: ComputeMarketCompensationDataOptions<T>
) => {
  return map(options.weightedJobs, async (item) => {
    // Filter only employees with the given job
    const datasetEmployeesWithJob = datasetEmployees.filter(hasJob([item.job.id]));

    // Get distinct count of companies
    const distinctCompaniesIds = chain(datasetEmployeesWithJob)
      .map(({ companyId, churnedCompanyId }) => companyId ?? churnedCompanyId)
      .uniq()
      .value();

    // Compute each measure & its compensation stats
    const measures = await computeDatasetEmployeesCompensationData(ctx, datasetEmployeesWithJob, {
      ...options,
      jobs: [item.job],
    });

    if (!measures) {
      return { job: item.job, weight: item.weight };
    }

    // Ensure there are enough employees & companies
    if (
      !options.fallbackToPredictiveData &&
      hasInsufficientDataQuality(datasetEmployeesWithJob.length, distinctCompaniesIds.length)
    ) {
      return { job: item.job, weight: item.weight };
    }

    return {
      job: item.job,
      weight: item.weight,
      employeesCount: datasetEmployeesWithJob.length,
      distinctCompaniesIds,
      measures,
    };
  });
};
